import { useState, useEffect } from 'react'
import { Table, Empty, Spin } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
    getStockCapitalFlow,
    getStockShortVolume,
    getStockShortInterest,
    getStockInstitutionalHolders,
    getStockInsiderTrades,
    getStockBuybacks,
    getStockDividends,
} from '@/api'
import type {
    CapitalFlowItem,
    ShortVolumeItem,
    ShortInterestItem,
    InstitutionalHolderItem,
    InsiderTradeItem,
    BuybackItem,
    DividendItem,
} from '@/api'
import './index.scss'

// ── 格式化辅助 ──────────────────────────────────────────────

const fmtAmount = (v: number | null): string => {
    if (v == null) return '--'
    const abs = Math.abs(v)
    if (abs >= 1e8) return `${(v / 1e8).toFixed(2)}亿`
    if (abs >= 1e4) return `${(v / 1e4).toFixed(2)}万`
    return v.toFixed(2)
}
const fmtNum = (v: number | null, digits = 2): string =>
    v == null ? '--' : v.toFixed(digits)
const flowCls = (v: number | null): string => {
    if (v == null) return ''
    return v > 0 ? 'green' : v < 0 ? 'red' : ''
}

// ── 列定义 ──────────────────────────────────────────────────

const capitalColumns: ColumnsType<CapitalFlowItem> = [
    { title: '日期', dataIndex: 'flow_date', key: 'flow_date', width: 100,
        render: (v: string) => <span style={{ color: '#fff' }}>{v}</span> },
    { title: '主力净流入', dataIndex: 'main_in_flow', key: 'main_in_flow', align: 'right',
        render: (v: number | null) => <span className={flowCls(v)}>{fmtAmount(v)}</span> },
    { title: '超大单', dataIndex: 'super_in_flow', key: 'super_in_flow', align: 'right',
        render: (v: number | null) => <span className={flowCls(v)}>{fmtAmount(v)}</span> },
    { title: '大单', dataIndex: 'big_in_flow', key: 'big_in_flow', align: 'right',
        render: (v: number | null) => <span className={flowCls(v)}>{fmtAmount(v)}</span> },
]

const shortVolumeColumns: ColumnsType<ShortVolumeItem> = [
    { title: '日期', dataIndex: 'trade_date', key: 'trade_date', width: 100,
        render: (v: string) => <span style={{ color: '#fff' }}>{v}</span> },
    { title: '卖空占比', dataIndex: 'short_ratio', key: 'short_ratio', align: 'right',
        render: (v: number | null) => (v != null ? `${fmtNum(v)}%` : '--') },
    { title: '卖空股数', dataIndex: 'short_sell_shares', key: 'short_sell_shares', align: 'right',
        render: (v: number | null) => fmtAmount(v) },
    { title: '累计做空占比', dataIndex: 'aggregated_short_ratio', key: 'aggregated_short_ratio', align: 'right',
        render: (v: number | null) => (v != null ? `${fmtNum(v)}%` : '--') },
]

const shortInterestColumns: ColumnsType<ShortInterestItem> = [
    { title: '日期', dataIndex: 'data_date', key: 'data_date', width: 100,
        render: (v: string) => <span style={{ color: '#fff' }}>{v}</span> },
    { title: '空头持仓占比', dataIndex: 'aggregated_short_ratio', key: 'aggregated_short_ratio', align: 'right',
        render: (v: number | null) => (v != null ? `${fmtNum(v)}%` : '--') },
    { title: '空头持仓股数', dataIndex: 'aggregated_short', key: 'aggregated_short', align: 'right',
        render: (v: number | null) => fmtAmount(v) },
    { title: '日均做空量', dataIndex: 'avg_daily_short_volume', key: 'avg_daily_short_volume', align: 'right',
        render: (v: number | null) => fmtAmount(v) },
]

const institutionalColumns: ColumnsType<InstitutionalHolderItem> = [
    { title: '报告期', dataIndex: 'period_text', key: 'period_text', width: 100,
        render: (v: string) => <span style={{ color: '#fff' }}>{v}</span> },
    { title: '机构持仓占比', dataIndex: 'holder_pct', key: 'holder_pct', align: 'right',
        render: (v: number | null) => (v != null ? `${fmtNum(v)}%` : '--') },
    { title: '持仓机构数', dataIndex: 'institution_count', key: 'institution_count', align: 'right',
        render: (v: number | null) => v ?? '--' },
    { title: '占比环比变动', dataIndex: 'holder_pct_change', key: 'holder_pct_change', align: 'right',
        render: (v: number | null) => (
            <span className={flowCls(v)}>
                {v != null ? `${v > 0 ? '+' : ''}${fmtNum(v)}pp` : '--'}
            </span>
        ) },
]

const insiderColumns: ColumnsType<InsiderTradeItem> = [
    { title: '日期', dataIndex: 'min_trade_date', key: 'min_trade_date', width: 100,
        render: (v: string) => <span style={{ color: '#fff' }}>{v}</span> },
    { title: '持有人', dataIndex: 'holder_name', key: 'holder_name',
        render: (v: string | null) => v ?? '--' },
    { title: '交易类型', dataIndex: 'transaction_type', key: 'transaction_type', width: 80,
        render: (v: string) => (
            <span className={v === 'BUY' ? 'green' : 'red'}>{v === 'BUY' ? '买入' : '卖出'}</span>
        ) },
    { title: '交易股数', dataIndex: 'trade_shares', key: 'trade_shares', align: 'right',
        render: (v: number | null) => fmtAmount(v) },
]

const buybackColumns: ColumnsType<BuybackItem> = [
    { title: '发布日', dataIndex: 'publ_date', key: 'publ_date', width: 100,
        render: (v: string) => <span style={{ color: '#fff' }}>{v}</span> },
    { title: '回购股数', dataIndex: 'shares', key: 'shares', align: 'right',
        render: (v: number | null) => fmtAmount(v) },
    { title: '回购金额', dataIndex: 'amount', key: 'amount', align: 'right',
        render: (v: number | null, r: BuybackItem) => (v == null ? '--' : `${fmtAmount(v)}${r.currency ?? ''}`) },
    { title: '占总股本', dataIndex: 'percentage', key: 'percentage', align: 'right',
        render: (v: number | null) => (v != null ? `${fmtNum(v)}%` : '--') },
]

const dividendColumns: ColumnsType<DividendItem> = [
    { title: '除息日', dataIndex: 'ex_dividend_date', key: 'ex_dividend_date', width: 100,
        render: (v: string | null) => <span style={{ color: '#fff' }}>{v ?? '--'}</span> },
    { title: '每股派息', dataIndex: 'dividend_per_share', key: 'dividend_per_share', align: 'right',
        render: (v: number | null, r: DividendItem) => (v == null ? '--' : `${fmtNum(v)}${r.currency ?? ''}`) },
    { title: '财年', dataIndex: 'fiscal_year', key: 'fiscal_year',
        render: (v: string | null) => v ?? '--' },
]

// ── 子组件：监控分组 ────────────────────────────────────────

interface MonitorGroupProps<T> {
    title: string
    loading: boolean
    error: boolean
    data: T[]
    columns: ColumnsType<T>
    rowKey: string | ((record: T) => React.Key)
    emptyHint?: string
}

function MonitorGroup<T>({ title, loading, error, data, columns, rowKey, emptyHint }: MonitorGroupProps<T>) {
    return (
        <div className="dynamic-monitoring-item">
            <div className="dynamic-monitoring-item-title">{title}</div>
            <Spin spinning={loading}>
                {error ? (
                    <div className="dynamic-monitoring__empty"><Empty description="数据加载失败" /></div>
                ) : data.length > 0 ? (
                    <Table<T>
                        rowKey={rowKey as string}
                        dataSource={data}
                        columns={columns}
                        pagination={false}
                        size="small"
                        className="dynamic-monitoring__table"
                    />
                ) : (
                    <div className="dynamic-monitoring__empty">
                        <Empty description={emptyHint ?? '暂无数据'} />
                    </div>
                )}
            </Spin>
        </div>
    )
}

// ── 主组件 ──────────────────────────────────────────────────

export default function DynamicMonitoring({ stockCode, onCountChange }: {
    stockCode: string
    onCountChange?: (n: number) => void
}) {
    const [loading, setLoading] = useState(true)
    const [capitalFlow, setCapitalFlow] = useState<CapitalFlowItem[]>([])
    const [shortVolume, setShortVolume] = useState<ShortVolumeItem[]>([])
    const [shortInterest, setShortInterest] = useState<ShortInterestItem[]>([])
    const [institutional, setInstitutional] = useState<InstitutionalHolderItem[]>([])
    const [insider, setInsider] = useState<InsiderTradeItem[]>([])
    const [buybacks, setBuybacks] = useState<BuybackItem[]>([])
    const [dividends, setDividends] = useState<DividendItem[]>([])
    const [errors, setErrors] = useState<Record<string, boolean>>({})

    useEffect(() => {
        setLoading(true)
        const errs: Record<string, boolean> = {}
        Promise.allSettled([
            getStockCapitalFlow(stockCode, { limit: 8 }).then(setCapitalFlow).catch(() => { errs.capital = true }),
            getStockShortVolume(stockCode, { limit: 8 }).then(setShortVolume).catch(() => { errs.shortVol = true }),
            getStockShortInterest(stockCode, 8).then(setShortInterest).catch(() => { errs.shortInt = true }),
            getStockInstitutionalHolders(stockCode, 8).then(setInstitutional).catch(() => { errs.inst = true }),
            getStockInsiderTrades(stockCode, 8).then(setInsider).catch(() => { errs.insider = true }),
            getStockBuybacks(stockCode, { limit: 5 }).then(setBuybacks).catch(() => { errs.buyback = true }),
            getStockDividends(stockCode, 5).then(setDividends).catch(() => { errs.div = true }),
        ]).then(() => {
            setErrors(errs)
            setLoading(false)
        })
    }, [stockCode])

    // 统计有数据的分组数（5 个逻辑分组），上报给父级 badge
    useEffect(() => {
        let count = 0
        if (capitalFlow.length > 0) count++
        if (shortVolume.length > 0 || shortInterest.length > 0) count++
        if (institutional.length > 0) count++
        if (insider.length > 0) count++
        if (buybacks.length > 0 || dividends.length > 0) count++
        onCountChange?.(count)
    }, [capitalFlow, shortVolume, shortInterest, institutional, insider, buybacks, dividends, onCountChange])

    return (
        <div className="dynamic-monitoring">
            <div className="dynamic-monitoring__body">
                <MonitorGroup
                    title="资金流向监控"
                    loading={loading}
                    error={errors.capital}
                    data={capitalFlow}
                    columns={capitalColumns}
                    rowKey="flow_date"
                />
                <MonitorGroup
                    title="卖空与空头监控"
                    loading={loading}
                    error={errors.shortVol && errors.shortInt}
                    data={shortVolume}
                    columns={shortVolumeColumns}
                    rowKey="trade_date"
                    emptyHint={shortInterest.length === 0 ? '暂无数据' : '暂无卖空数据'}
                />
                {shortInterest.length > 0 && (
                    <div className="dynamic-monitoring-item">
                        <div className="dynamic-monitoring-item-subtitle">空头持仓</div>
                        <Table<ShortInterestItem>
                            rowKey="data_date"
                            dataSource={shortInterest}
                            columns={shortInterestColumns}
                            pagination={false}
                            size="small"
                            className="dynamic-monitoring__table"
                        />
                    </div>
                )}
                <MonitorGroup
                    title="机构持仓监控"
                    loading={loading}
                    error={errors.inst}
                    data={institutional}
                    columns={institutionalColumns}
                    rowKey="period_text"
                />
                <MonitorGroup
                    title="内部人交易监控"
                    loading={loading}
                    error={errors.insider}
                    data={insider}
                    columns={insiderColumns}
                    rowKey={(r) => `${r.min_trade_date}-${r.holder_id}`}
                    emptyHint="仅美股有数据"
                />
                <MonitorGroup
                    title="回购与分红监控"
                    loading={loading}
                    error={errors.buyback && errors.div}
                    data={buybacks}
                    columns={buybackColumns}
                    rowKey="publ_date"
                    emptyHint={dividends.length === 0 ? '暂无数据' : '暂无回购数据'}
                />
                {dividends.length > 0 && (
                    <div className="dynamic-monitoring-item">
                        <div className="dynamic-monitoring-item-subtitle">分红历史</div>
                        <Table<DividendItem>
                            rowKey="ex_dividend_date"
                            dataSource={dividends}
                            columns={dividendColumns}
                            pagination={false}
                            size="small"
                            className="dynamic-monitoring__table"
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
